// The Sold table's column-visibility toggle: which of its mutable columns are offered at all, and
// which of those default on. Pure, no Vue dependency - same shape as
// `pigsSlaughters/useSlaughtersColumnVisibility.ts`. Section/Customer/Invoice are hard-gated, exactly
// preserving this page's previous behaviour (before it had any manual toggle at all): a directory
// with none of a fact simply never offers it. Receiving CHR is not gated - it stays offered
// regardless of data, so a reader can turn it on ahead of data showing up for it. The generic
// mechanics (storage, toggling, select-all) live in the shared `useColumnVisibilityOverrides`
// composable instead.

import type { SoldRow } from '@/types';

/** The columns a reader can show or hide. Fixed columns (date/location/number/weights) are never in
 * here. */
export type SoldToggleColumnId = 'section' | 'customer' | 'invoice' | 'receiving_chr' | 'created_by';

/** Left-to-right table order of the mutable columns. */
export const TOGGLE_COLUMN_IDS: readonly SoldToggleColumnId[] = ['section', 'customer', 'invoice', 'receiving_chr', 'created_by'];

/**
 * The columns whose availability - not just their default state - depends on the loaded rows: one
 * with nothing to show in any of them is dropped from the toggle list entirely, rather than merely
 * defaulting off.
 */
const GATED_COLUMN_IDS: ReadonlySet<SoldToggleColumnId> = new Set(['section', 'customer', 'invoice']);

export type GatedColumnId = 'section' | 'customer' | 'invoice';

/** Whether each toggleable column has at least one non-null entry among the given rows. */
export function columnPresence(rows: SoldRow[]): Record<SoldToggleColumnId, boolean> {
    const present = (pick: (row: SoldRow) => unknown) => rows.some((row) => pick(row) !== null);

    return {
        section: present((row) => row.section),
        customer: present((row) => row.customer),
        invoice: present((row) => row.invoice),
        receiving_chr: present((row) => row.receiving_chr),
        created_by: present((row) => row.created_by),
    };
}

/** A column is offered in the dropdown at all - Section/Customer/Invoice only once the loaded rows
 * carry them; Receiving CHR is always offered. */
export function isColumnAvailable(columnId: SoldToggleColumnId, presence: Record<GatedColumnId, boolean>): boolean {
    return GATED_COLUMN_IDS.has(columnId) ? presence[columnId as GatedColumnId] : true;
}
